import {
  ForwardRefRenderFunction,
  ForwardedRef,
  MutableRefObject,
  RefObject,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { MapLayerMouseEvent } from 'react-map-gl/maplibre';
import { ReusableComponentsVehicleModeEnum } from '../../generated/graphql';
import {
  MapEntityEditorViewState,
  MapEntityType,
  isPlacingOrMoving,
  selectHasDraftLocation,
  selectMapDepotStopViewState,
  selectMapFilter,
  selectMapStopAreaViewState,
  selectMapStopViewState,
  selectMapTerminalViewState,
  selectSelectedRouteId,
  selectShowMapEntityTypes,
  setSelectedRouteIdAction,
  useAppDispatch,
  useAppSelector,
} from '../../redux';
import { CustomOverlay } from './CustomOverlay';
import { ItemTypeFiltersOverlay, MapFilterPanel } from './Filters';
import { InfraLinksVectorLayer } from './InfraLinksVectorLayer';
import { Maplibre } from './Maplibre';
import { useGetMapData } from './Queries';
import {
  RouteEditorRef,
  StopAreasRef,
  StopsRef,
  TerminalsRef,
} from './refTypes';
import { Routes, isRouteGeometryLayer, mapLayerIdToRouteId } from './Routes';
import { FunctionalAreaVisualization, StopAreas } from './StopAreas';
import { MemberStopLines, Stops } from './Stops';
import { Terminals } from './Terminals';
import { useMapUrlStateContext } from './Utils/mapUrlState';

type MapViewState = {
  readonly mapStopViewState: MapEntityEditorViewState;
  readonly mapStopAreaViewState: MapEntityEditorViewState;
  readonly mapTerminalViewState: MapEntityEditorViewState;
  readonly mapDepotStopViewState: MapEntityEditorViewState;
};

type EditorRefs = {
  readonly routeEditorRef: MutableRefObject<RouteEditorRef | null>;
  readonly stopsRef: MutableRefObject<StopsRef | null>;
  readonly stopAreasRef: MutableRefObject<StopAreasRef | null>;
  readonly terminalsRef: MutableRefObject<TerminalsRef | null>;
};

function useEditorRefs(): EditorRefs {
  return {
    routeEditorRef: useRef(null),
    stopsRef: useRef(null),
    stopAreasRef: useRef(null),
    terminalsRef: useRef(null),
  };
}

function useRouteEditorImperativeHandle(
  ref: ForwardedRef<RouteEditorRef>,
  routeEditorRef: RefObject<RouteEditorRef>,
) {
  useImperativeHandle(ref, () => ({
    onDrawRoute: (vehicleMode: ReusableComponentsVehicleModeEnum) => {
      routeEditorRef.current?.onDrawRoute(vehicleMode);
    },
    onEditRoute: () => {
      routeEditorRef.current?.onEditRoute();
    },
    onStopEditRoute: () => {
      routeEditorRef.current?.onStopEditRoute();
    },
    onDeleteRoute: () => {
      routeEditorRef.current?.onDeleteRoute();
    },
    onCancel: () => {
      routeEditorRef.current?.onCancel();
    },
    onSave: () => {
      routeEditorRef.current?.onSave();
    },
  }));
}

function useMapViewState(): MapViewState {
  return {
    mapStopViewState: useAppSelector(selectMapStopViewState),
    mapStopAreaViewState: useAppSelector(selectMapStopAreaViewState),
    mapTerminalViewState: useAppSelector(selectMapTerminalViewState),
    mapDepotStopViewState: useAppSelector(selectMapDepotStopViewState),
  };
}

function useHandleMapRouteClick() {
  const dispatch = useAppDispatch();
  const routeIsSelected = !!useAppSelector(selectSelectedRouteId);

  return (e: MapLayerMouseEvent) => {
    // Retrieve all rendered features on the map
    const { features } = e;

    // If the clicked feature was a route, select it
    if (features?.length) {
      const routeFeatures = features.filter((feature) =>
        isRouteGeometryLayer(feature.layer.id),
      );

      if (routeFeatures.length) {
        const routeId = mapLayerIdToRouteId(routeFeatures[0].layer.id);

        // set clicked route as selected
        dispatch(setSelectedRouteIdAction(routeId));
      }

      return;
    }

    // If clicked away, unselect route (if any)
    if (routeIsSelected) {
      dispatch(setSelectedRouteIdAction(undefined));
    }
  };
}

function useOnClickMap(
  { stopsRef, stopAreasRef, terminalsRef }: EditorRefs,
  {
    mapStopViewState,
    mapStopAreaViewState,
    mapTerminalViewState,
    mapDepotStopViewState,
  }: MapViewState,
) {
  const handleMapRouteClick = useHandleMapRouteClick();

  return async (e: MapLayerMouseEvent): Promise<void> => {
    if (mapDepotStopViewState === MapEntityEditorViewState.PLACE) {
      return stopsRef.current?.onPlaceDepotStop(e);
    }

    if (mapDepotStopViewState === MapEntityEditorViewState.MOVE) {
      return stopsRef.current?.onMoveDepotStop(e);
    }

    if (mapStopViewState === MapEntityEditorViewState.PLACE) {
      return stopsRef.current?.onCreateStop(e);
    }

    if (mapStopViewState === MapEntityEditorViewState.PLACECOPY) {
      return stopsRef.current?.onCopyStop(e);
    }

    if (mapStopViewState === MapEntityEditorViewState.MOVE) {
      return stopsRef.current?.onMoveStop(e);
    }

    if (mapStopAreaViewState === MapEntityEditorViewState.PLACE) {
      return stopAreasRef.current?.onCreateStopArea(e);
    }

    if (mapStopAreaViewState === MapEntityEditorViewState.MOVE) {
      return stopAreasRef.current?.onMoveStopArea(e);
    }

    if (mapTerminalViewState === MapEntityEditorViewState.PLACE) {
      return terminalsRef.current?.onCreateTerminal(e);
    }

    if (mapTerminalViewState === MapEntityEditorViewState.MOVE) {
      return terminalsRef.current?.onMoveTerminal(e);
    }

    return handleMapRouteClick(e);
  };
}

type MapProps = {
  readonly width?: string;
  readonly height?: string;
};

export const MapComponent: ForwardRefRenderFunction<
  RouteEditorRef,
  MapProps
> = ({ width = '100vw', height = '100vh' }, externalRef) => {
  const [showRoute, setShowRoute] = useState(true);
  const {
    state: {
      displayedRoute: { routeId: urlRouteId },
    },
  } = useMapUrlStateContext();
  const dispatch = useAppDispatch();

  const editorRefs = useEditorRefs();
  const mapViewState = useMapViewState();

  useEffect(() => {
    if (urlRouteId) {
      dispatch(setSelectedRouteIdAction(urlRouteId));
    }
  }, [dispatch, urlRouteId]);

  useRouteEditorImperativeHandle(externalRef, editorRefs.routeEditorRef);
  const onClick = useOnClickMap(editorRefs, mapViewState);

  const hasDraftStopLocation = useAppSelector(selectHasDraftLocation);
  const { showMapEntityTypeFilterOverlay } = useAppSelector(selectMapFilter);
  const showMapEntityTypes = useAppSelector(selectShowMapEntityTypes);
  const showBusNetwork = showMapEntityTypes[MapEntityType.BusNetwork];
  const showTramNetwork = showMapEntityTypes[MapEntityType.TramNetwork];
  const showInfraLinks = showBusNetwork || showTramNetwork;

  const { areas, displayedRouteIds, stops, terminals, depotStops } =
    useGetMapData();

  return (
    <Maplibre width={width} height={height} onClick={onClick}>
      <Stops
        areas={areas}
        stops={stops}
        terminals={terminals}
        depotStops={depotStops}
        displayedRouteIds={displayedRouteIds}
        showRoute={showRoute}
        ref={editorRefs.stopsRef}
      />
      <StopAreas areas={areas} ref={editorRefs.stopAreasRef} />
      <Terminals terminals={terminals} ref={editorRefs.terminalsRef} />

      <MemberStopLines areas={areas} stops={stops} terminals={terminals} />

      <CustomOverlay
        className="col-span-2 justify-self-start"
        position="top-left"
      >
        <MapFilterPanel
          routeDisplayed={!!displayedRouteIds.length}
          showRoute={showRoute}
          setShowRoute={setShowRoute}
          className="pointer-events-auto shadow-md"
        />
      </CustomOverlay>

      {showMapEntityTypeFilterOverlay && (
        <CustomOverlay order={2} position="top-left">
          <ItemTypeFiltersOverlay className="pointer-events-auto mb-2" />
        </CustomOverlay>
      )}

      <InfraLinksVectorLayer
        enableInfraLinkLayer={
          showInfraLinks ||
          isPlacingOrMoving(mapViewState.mapStopViewState) ||
          hasDraftStopLocation
        }
        showBusNetwork={showBusNetwork}
        showTramNetwork={showTramNetwork}
      />

      <Routes
        displayedRouteIds={displayedRouteIds}
        showRoute={showRoute}
        ref={editorRefs.routeEditorRef}
      />

      <FunctionalAreaVisualization stops={stops} />
    </Maplibre>
  );
};

export const Map = forwardRef(MapComponent);
